import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import dayjs, { Dayjs } from "dayjs";

import { dateNormalizer, errorNormalizer } from "../../../../shared/Helpers/functions";
import { CustomAlert, CustomButton, Picker } from "../../../../shared/uiComponents";
import { useCheckPermission } from "../../../../shared/Helpers/hooks";
import { AdminTypes } from "../../../../components/Menu";
import { ButtonWrapper } from "../../../../components/StyledComponents";
import { useResetCalendarData } from "../../Helpers/hooks";
import { PERMISSIONS } from "../../../../App/constants";

import { DispatchProperties, useSelector } from "../../../../redux/store";
import { setInfoEvent } from "../../../../redux/State/clientSlice/calendarSlice";
import { getSoapNotePDF } from "../../../../redux/State/exportSlice";
import { clearErrors, createSoapNote } from "../../../../redux/State/clientSlice/soapNoteSlice";
import { SoapNotesStatuses } from "../../../../redux/API/ClientAPIHelpers/dataCollectionProperties";
import { clearSessionInfo, unlockSession } from "../../../../redux/State/clientSlice/sessionSlice";

const initialExpirationDate = dayjs().add(2, "days");

const SoapNoteStatus = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<DispatchProperties>();
  const { resetData } = useResetCalendarData();
  const { permissionGranted } = useCheckPermission(PERMISSIONS.SOAPNOTE.UNLOCK);

  const loadingExport = useSelector((state) => state.export.loading);
  const loading = useSelector((state) => state.session.loading);
  const data = useSelector((state) => state.calendar.infoEventData);
  const role = useSelector((state) => state.account.role);
  const userId = useSelector((state) => state.account.user.id);

  const [unlockConfirm, setUnlockConfirm] = useState<boolean>(false);
  const [expirationDateValue, setExpirationDateValue] = useState<Dayjs | null>(
    initialExpirationDate
  );

  const expired =
    SoapNotesStatuses.expired48 === data?.reportStatus?.status ||
    SoapNotesStatuses.expiredWeek === data?.reportStatus?.status;

  const reportStatus = data?.reportStatus?.status;

  const navigateEditSoapNote = ({
    clientId,
    reportId,
    sessionId,
  }: {
    clientId: string;
    reportId: string;
    sessionId: string;
  }) => {
    dispatch(setInfoEvent(false));
    dispatch(clearSessionInfo());
    dispatch(clearErrors());
    navigate(`/soap-notes/info?clientId=${clientId}&reportId=${reportId}&sessionId=${sessionId}`);
  };

  const editSoapNote = () => {
    if (!data) return;
    const { client, reportId, sessionId } = data;
    if (!reportId) return;
    navigateEditSoapNote({ clientId: client.id, reportId, sessionId });
  };

  const viewSoapNoteHandler = () => {
    if (!data?.reportId) return;
    const reportId = data.reportId;
    dispatch(
      getSoapNotePDF({
        reportId,
        includeTrials: true,
      })
    )
      .then(unwrapResult)
      .then((response) => {
        const objectURL = URL.createObjectURL(response);
        window.open(objectURL);
      })
      .catch(errorNormalizer);
  };

  const unlockHandler = () => {
    if (!data?.sessionId) return;
    const sessionId = data.sessionId;

    dispatch(
      unlockSession({
        sessionId,
        nextExpirationDate: dateNormalizer(dayjs(expirationDateValue)),
      })
    )
      .then(unwrapResult)
      .then(() => {
        resetData();
        toast("Success");
      })
      .catch(errorNormalizer);
  };

  const findButtonTitle = (status: number | undefined) => {
    switch (status) {
      case SoapNotesStatuses.draft:
        return "Soap note incomplete";
      case SoapNotesStatuses.expired48 | SoapNotesStatuses.expiredWeek:
        return "Soap note expired";
      case SoapNotesStatuses.submitted:
        return "Soap note completed";
      case SoapNotesStatuses.unlocked:
        return "Soap note unlocked";
      case SoapNotesStatuses.pendingBT:
        return "Soap note pending BT";
      case SoapNotesStatuses.timeMismatch:
        return "Time/Location mismatch";
      default:
        return "Start soap note";
    }
  };

  const createSoapNoteHandler = () => {
    if (!data) return;

    const { client, sessionId } = data;
    dispatch(createSoapNote({ clientId: client.id, sessionId }))
      .then(unwrapResult)
      .then(({ reportId }) => {
        if (!reportId) return;
        navigateEditSoapNote({ clientId: client.id, reportId, sessionId });
      })
      .catch(errorNormalizer);
  };

  return (
    <>
      <CustomButton
        title={findButtonTitle(reportStatus)}
        loading={loading || loadingExport}
        disabled={expired || (userId !== data?.user.id && role?.section.id !== AdminTypes.admin)}
        className="marginTop8"
        onClick={
          typeof reportStatus === "number"
            ? SoapNotesStatuses.submitted === reportStatus
              ? viewSoapNoteHandler
              : editSoapNote
            : createSoapNoteHandler
        }
      />
      {expired && role?.section.id === AdminTypes.admin && permissionGranted && (
        <CustomButton
          title="Unlock soap note"
          onClick={() => setUnlockConfirm(true)}
          className="marginTop8"
          loading={loading}
        />
      )}
      <CustomAlert
        title="Set next expiration date"
        onClose={() => setUnlockConfirm(false)}
        open={unlockConfirm}
        Content={() => (
          <>
            <ButtonWrapper>
              <Picker.CustomDate
                label="Expiration date"
                value={expirationDateValue}
                onChange={setExpirationDateValue}
              />
            </ButtonWrapper>
            <ButtonWrapper>
              <CustomButton
                secondaryButton
                title="Cancel"
                onClick={() => setUnlockConfirm(false)}
              />
              <CustomButton title="Unlock" onClick={unlockHandler} />
            </ButtonWrapper>
          </>
        )}
      />
    </>
  );
};

export default SoapNoteStatus;
